import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { setSeo, setJsonLd, clearJsonLd, track } from "@/lib/api";
import { OfferGrid } from "@/components/site/OfferGrid";
import { MmImage, useMedicalFavicon } from "@/components/medical/MedicalChrome";
import {
  MedicalBottomCta,
  MedicalTopCta,
  useMedicalOffers,
} from "@/components/medical/MedicalPartnerCtas";
import { MedicalDisclaimer } from "@/pages/medical/MedicalHome";
import {
  CostGuide,
  PartnershipInvite,
  QuestionsToAsk,
} from "@/components/medical/MedicalInfoBlocks";
import {
  MM_BRAND,
  MM_CATEGORIES,
  MM_ROOT,
  mmCategory,
  mmPath,
} from "@/data/medicalMobility";
// An unknown slug under the section keeps the visitor inside Medical & Mobility rather than
// handing them the main site's 404 and its links back out to the homepage.
const MedicalNotFound = () => {
  useEffect(() => {
    setSeo({
      title: `Page not found — ${MM_BRAND.division} | ${MM_BRAND.name}`,
      description: "This Medical & Mobility page does not exist.",
      noindex: true,
      follow: false,
    });
  }, []);
  return (
    <div data-testid="mm-not-found" className="max-w-[820px] mx-auto px-5 md:px-8 py-24 md:py-32">
      <span className="eyebrow">{MM_BRAND.division}</span>
      <h1 className="display text-[38px] md:text-[52px] leading-[1.05] text-charcoal mt-5">
        This page does not exist
      </h1>
      <p className="text-[17px] md:text-[19px] font-light leading-[1.55] text-[#4F4F4F] mt-5">
        The equipment category you were looking for is not here. Every Medical &amp; Mobility
        category is listed on the section home page.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 mt-9">
        <Link
          to={MM_ROOT}
          data-testid="mm-not-found-home"
          className="inline-flex items-center justify-center gap-2 px-7 py-4 text-[12.5px] tracking-[0.14em] uppercase font-semibold bg-charcoal text-white hover:bg-sage transition-colors duration-300"
        >
          Medical &amp; Mobility Home <ArrowRight size={15} />
        </Link>
        <Link
          to={`${MM_ROOT}#categories`}
          data-testid="mm-not-found-categories"
          className="inline-flex items-center justify-center gap-2 px-7 py-4 text-[12.5px] tracking-[0.14em] uppercase font-semibold border border-charcoal text-charcoal hover:bg-charcoal hover:text-white transition-colors duration-300"
        >
          Browse All Categories
        </Link>
      </div>
    </div>
  );
};

export default function MedicalCategory() {
  const { slug } = useParams();
  const category = mmCategory(slug);
  const offers = useMedicalOffers(category?.offerCategory);
  useMedicalFavicon();

  useEffect(() => {
    if (!category) return;
    setSeo({
      title: category.seo.title,
      description: category.seo.description,
      image: `${window.location.origin}${category.image.base}-1024.webp`,
    });
    setJsonLd("mm-schema", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: MM_BRAND.division,
          item: window.location.origin + MM_ROOT,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: category.name,
          item: window.location.origin + mmPath(category.slug),
        },
      ],
    });
    track({ event_type: "page_view", page: mmPath(category.slug) });
    return () => clearJsonLd("mm-schema");
  }, [category]);

  if (!category) return <MedicalNotFound />;

  const related = MM_CATEGORIES.filter((c) => c.slug !== category.slug);

  return (
    <div data-testid={`mm-category-${category.slug}`}>
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-7">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[12.5px] text-[#6E6A63]">
          <Link to={MM_ROOT} data-testid="mm-breadcrumb-home" className="hover:text-sage-dark transition-colors">
            Medical &amp; Mobility
          </Link>
          <ChevronRight size={13} className="shrink-0" />
          <span className="text-charcoal">{category.name}</span>
        </nav>
      </div>

      <header className="max-w-[1280px] mx-auto px-5 md:px-8 pt-8 md:pt-10">
        <div className="grid lg:grid-cols-[46fr_54fr] gap-8 lg:gap-12 items-center">
          <div>
            <span
              data-testid="mm-category-eyebrow"
              className="text-[11px] tracking-[0.18em] uppercase text-sage-dark font-semibold"
            >
              {MM_BRAND.division}
            </span>
            <h1
              data-testid="mm-category-heading"
              className="display text-[34px] sm:text-[44px] lg:text-[52px] leading-[1.06] text-charcoal mt-4"
            >
              {category.name}
            </h1>
            <p className="text-[17px] md:text-[19px] font-light leading-[1.55] text-[#454545] mt-5">
              {category.lede}
            </p>
            <MedicalTopCta offers={offers} category={category.offerCategory} />
          </div>
          <div className="overflow-hidden aspect-[4/3] lg:aspect-[3/2]">
            <MmImage
              base={category.image.base}
              alt={category.image.alt}
              widths={category.image.widths}
              priority
              sizes="(min-width:1024px) 54vw, 100vw"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </header>

      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 md:py-16">
        <section data-testid="mm-category-overview" className="max-w-3xl">
          <span className="eyebrow">Overview</span>
          {category.overview.map((t) => (
            <p key={t} className="text-[16.5px] md:text-[17.5px] leading-[1.65] text-[#3F3F3F] mt-5">
              {t}
            </p>
          ))}
        </section>

        {/* Approved partners only, placed directly under the overview so a visitor sees the
            affiliate options without hunting for them. Renders nothing until an active offer with
            a tracking URL exists for this category. */}
        {category.informational ? (
          <p
            data-testid="mm-informational-note"
            className="mt-10 border-l-2 border-sage pl-5 text-[15.5px] leading-[1.65] text-[#4F4B45] max-w-3xl"
          >
            {category.informationalNote}
          </p>
        ) : (
          <OfferGrid
            category={category.offerCategory}
            heading="Shop this category"
            intro={`Approved ${MM_BRAND.division} partners for this equipment category.`}
            id="partner-options"
            placement="MEDICAL CATEGORY GRID"
            singleWidthClass="max-w-2xl"
            rel="sponsored noopener noreferrer"
            ctaBlock
            disclosure
          />
        )}

        {category.subPages?.length > 0 && (
          <section data-testid="mm-subpages" className="mt-14 md:mt-16">
            <span className="eyebrow">Go deeper</span>
            <div className="grid sm:grid-cols-2 gap-5 mt-6">
              {category.subPages.map((slug) => {
                const sub = mmCategory(slug);
                if (!sub) return null;
                return (
                  <Link
                    key={slug}
                    to={mmPath(slug)}
                    data-testid={`mm-subpage-${slug}`}
                    className="group bg-white border border-line px-6 py-6 hover:border-sage transition-colors duration-300"
                  >
                    <h3 className="text-[17.5px] font-semibold text-charcoal">{sub.name}</h3>
                    <p className="text-[15px] leading-[1.6] text-[#4F4B45] mt-2.5">
                      {sub.cardText}
                    </p>
                    <span className="inline-flex items-center gap-2 mt-4 text-[11.5px] tracking-[0.14em] uppercase font-semibold text-charcoal group-hover:text-sage-dark transition-colors">
                      Read the guide
                      <ArrowRight
                        size={14}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <section
          id="equipment-types"
          data-testid="mm-category-equipment"
          className="mt-16 md:mt-20 border-t border-line pt-12 scroll-mt-28"
        >
          <span className="eyebrow">Main equipment types</span>
          <h2 className="display text-[29px] md:text-[37px] leading-[1.1] text-charcoal mt-5 max-w-2xl">
            What this category includes
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-9 mt-10">
            {category.equipment.map((e) => (
              <div key={e.h} className="border-t-[3px] border-t-sage pt-5">
                <h3 className="text-[17.5px] font-semibold text-charcoal">{e.h}</h3>
                <p className="text-[15.5px] leading-[1.65] text-[#4F4B45] mt-2.5">{e.p}</p>
              </div>
            ))}
          </div>
          {category.sectionImage && (
            <figure
              data-testid="mm-category-section-image"
              className="mt-12 md:mt-14 overflow-hidden max-w-[1000px]"
              style={{
                aspectRatio: `${category.sectionImage.width} / ${category.sectionImage.height}`,
              }}
            >
              <MmImage
                base={category.sectionImage.base}
                alt={category.sectionImage.alt}
                width={category.sectionImage.width}
                height={category.sectionImage.height}
                widths={category.sectionImage.widths}
                sizes="(min-width:1280px) 1000px, 100vw"
                className="w-full h-full object-cover"
              />
            </figure>
          )}
        </section>

        <section
          id="what-to-consider"
          data-testid="mm-category-considerations"
          className="mt-16 md:mt-20 border-t border-line pt-12 scroll-mt-28"
        >
          <span className="eyebrow">Before you buy</span>
          <h2 className="display text-[29px] md:text-[37px] leading-[1.1] text-charcoal mt-5 max-w-2xl">
            What to consider before purchasing
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 mt-10">
            {category.considerations.map((c) => (
              <div key={c.h}>
                <h3 className="text-[16.5px] font-semibold text-charcoal">{c.h}</h3>
                <p className="text-[15.5px] leading-[1.65] text-[#4F4B45] mt-2">{c.p}</p>
              </div>
            ))}
          </div>
        </section>

        {category.costs && (
          <CostGuide costs={category.costs} testId={`mm-costs-${category.slug}`} />
        )}

        {category.questions?.length > 0 && (
          <QuestionsToAsk
            questions={category.questions}
            heading={
              category.slug === "home-elevators"
                ? "Questions to ask before requesting a quote"
                : "Questions to ask before purchasing"
            }
            testId={`mm-questions-${category.slug}`}
          />
        )}

        <section data-testid="mm-category-related" className="mt-16 md:mt-20 border-t border-line pt-12">
          <span className="eyebrow">Related Medical &amp; Mobility categories</span>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-7">
            {related.map((c) => (
              <Link
                key={c.slug}
                to={mmPath(c.slug)}
                data-testid={`mm-related-${c.slug}`}
                className="group flex items-center justify-between gap-4 bg-white border border-line px-6 py-5 hover:border-sage transition-colors duration-300"
              >
                <span className="text-[15.5px] leading-snug text-charcoal">{c.name}</span>
                <ArrowRight
                  size={16}
                  className="shrink-0 text-sage-dark transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            ))}
          </div>
        </section>

        <MedicalBottomCta offers={offers} category={category.offerCategory} />

        {category.partnership && (
          <PartnershipInvite
            variant={category.partnership}
            testId={`mm-partnership-${category.slug}`}
          />
        )}

        <MedicalDisclaimer testId={`mm-disclaimer-${category.slug}`} />
      </div>
    </div>
  );
}
